import ButtonSelection from '@/views/components/ButtonSelection/ButtonSelection.vue';
import { EntrancePlantDetailDTO, EntrancePlantStructureCreateDTO, GateConfigDTO, PeGateConfig, PlantConfigStep } from '@/../client-generator/generated-clients/plants';
import { Vue, Options } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import GateConfigComponent from '../GateConfigComponent/GateConfigComponent.vue';

@Options({
  name: 'DialogEntrancePlantCreateSetupGate',
  components: {
      ButtonSelection,
      GateConfigComponent
    },
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },
    structureCreate: {
      required: true,
      type: Object as () => EntrancePlantStructureCreateDTO,
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    plant: function (newValue) {
      if (newValue) this.initializeData();
    },
  },
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
})
export default class DialogEntrancePlantCreateSetupGate extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  structureCreate!: EntrancePlantStructureCreateDTO;
  readonly MIN_SCS_ADDRESS = 0;
  readonly MAX_SCS_ADDRESS = 95;
  gatesToConfigure: { [key: string]: GateConfigDTO } = {};
  anyScsError: boolean = false;

  get isBackboneConfiguration(): boolean {
    if (this.structureCreate.pePlantConfig?.currentStep === PlantConfigStep.BACKBONE_GATES_CONFIG) {
      this.structureCreate.previousWizardPage = 'DialogEntrancePlantCreateRiserOrNot';
      return true;
    }
    return false;
  }

  get pageTitle(): string {
    return this.$t('AppResourceEP.SetupGateTitleWithIndex') as string;
  }

  get labelDescription(): string {
    let riserName = '';
    if (this.isBackboneConfiguration) {
      riserName = this.$t('AppResourceEP.Backbone');
    } 
    else {
      riserName = '';
    }

    return this.$t('AppResourceEP.SetupGateParameterWithRiserName', [riserName]) as string;
  }

  get nextEnabled(): boolean {
    if (Object.keys(this.gatesToConfigure).length === 0) return false;
    
    return Object.values(this.gatesToConfigure).every((gate, index) => {
      const devicesSelected = (gate.newPeSelected === true || gate.keypadSelected === true || gate.smartReaderSelected === true);
      const scsValid = !this.anyScsError;
      return devicesSelected && scsValid;
    });
  }

  get availableScsAddressGate(): number[] {
    return this.plant.scsAddressHelper?.availableScsAddressGate ?? [];
  }

  mounted(): void {
    this.initializeData();
  }

  initializeData() {
    this.gatesToConfigure = {};
    let numberOfGates = 0;
    
    var pePlantConfigGates: PeGateConfig[] = [];
    if (this.isBackboneConfiguration) {
      pePlantConfigGates = (this.structureCreate.pePlantConfig!.pePlantConfig?.gates ?? []).filter(g => g.inBackboneInterface === true);
      numberOfGates = this.structureCreate.pePlantConfig?.numbersOfBackboneGates ?? 0;
    }
    else {
      numberOfGates = 0;
    }

    for (let i = 0; i < pePlantConfigGates.length; i++) {
      const gateId = pePlantConfigGates[i].id!;
      const existingConfig = this.structureCreate.gateConfigStructure?.[gateId];
      
      const gateConfig = new GateConfigDTO();
      gateConfig.gateName = existingConfig?.gateName ?? pePlantConfigGates[i].name;
      gateConfig.scsAddress = existingConfig?.scsAddress ?? this.getValidScsAddressForNewGate();
      gateConfig.isOnBackbone = existingConfig?.isOnBackbone ?? pePlantConfigGates[i].inBackboneInterface;
      gateConfig.newPeSelected = existingConfig?.newPeSelected ?? false;
      gateConfig.keypadSelected = existingConfig?.keypadSelected ?? false;
      gateConfig.smartReaderSelected = existingConfig?.smartReaderSelected ?? false;
      
      this.gatesToConfigure[gateId] = gateConfig;
    }
  }

  getValidScsAddressForNewGate(): number {
    const usedAddresses = Object.values(this.gatesToConfigure)
      .map((gate: GateConfigDTO) => gate.scsAddress!)
      .filter((addr: number | undefined) => addr !== undefined && addr !== null)
      .map((addr: number) => Number(addr));
    
    for (let addr = this.MIN_SCS_ADDRESS; addr <= this.MAX_SCS_ADDRESS; addr++) {
      const availableInPlant = this.plant.scsAddressHelper?.availableScsAddressGate?.includes(addr) ?? false;
      const usedByOtherGate = usedAddresses.includes(addr);
      
      if (availableInPlant && !usedByOtherGate) {
        return addr;
      }
    }
    
    return this.MAX_SCS_ADDRESS;
  }

  updateScsError(hasError: boolean) {
    this.anyScsError = hasError;
  }

  handleBackButton() {
    this.$emit('onBack', 'DialogEntrancePlantCreateRiserOrNot');
  }

  async handleNextButton() {
    await this.updatePlantStructure(this.getDataToSave());
    this.structureCreate.previousWizardPage = 'DialogEntrancePlantCreateSetupGate';
    if (this.isBackboneConfiguration) {
      this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.RISERS_CONFIG;
      this.$emit('onNext', 'DialogEntrancePlantCreateSetupRiser');
    } else {
      this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.BUILDINGS_CONFIG;
      this.$emit('onNext', 'DialogEntrancePlantCreateStep5Riser');
    }
  }

  getDataToSave(): EntrancePlantStructureCreateDTO {
    return new EntrancePlantStructureCreateDTO({
      gateConfigStructure: this.gatesToConfigure,
      pePlantConfig: this.structureCreate.pePlantConfig,
      riserFlow: true,
    });
  }

  async updatePlantStructure(structure: EntrancePlantStructureCreateDTO) {
    if (!this.plantId) return;
    this.$emit('is-loading', true);
    
    try {
      const result = await plantsClient.structure(this.plant.desktopId!, structure);
      this.structureCreate.pePlantConfig = result.pePlantConfig;
      this.structureCreate.gateConfigStructure = { ...this.structureCreate.gateConfigStructure, ...result.gateConfigStructure };
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('is-loading', false);
    }
  }
}
